import { MAP_LIB_BASE } from './runtimeEnv';

// Leaflet, loaded at runtime from MAP_LIB_BASE — once per document, shared by
// every map. The same approach as the clinic panel's LocationPicker: the map
// is optional, so the library stays out of the entry bundle and only a doctor
// who opens a map pays for it.
//
// A promise rather than a flag: two opens must not race two <script> tags, and
// a FAILED load forgets itself so the next open tries again instead of
// reporting a stale failure forever (a network inside Iran can lose the CDN
// for a minute).

const LOAD_TIMEOUT_MS = 15_000;
let pending = null;

export function loadLeaflet() {
  if (typeof window === 'undefined') return Promise.reject(new Error('no-window'));
  if (window.L) return Promise.resolve(window.L);
  if (pending) return pending;

  pending = new Promise((resolve, reject) => {
    const fail = (why) => { pending = null; reject(new Error(why)); };
    const timer = setTimeout(() => fail('timeout'), LOAD_TIMEOUT_MS);
    const done = (fn) => (arg) => { clearTimeout(timer); fn(arg); };

    if (!document.getElementById('leaflet-css')) {
      const link = document.createElement('link');
      link.id = 'leaflet-css';
      link.rel = 'stylesheet';
      link.href = `${MAP_LIB_BASE}/leaflet.css`;
      document.head.appendChild(link);
    }

    const existing = document.getElementById('leaflet-js');
    const script = existing || document.createElement('script');
    script.addEventListener('load', done(() => (window.L ? resolve(window.L) : fail('network'))));
    script.addEventListener('error', done(() => {
      // A failed tag would answer every later open with the same failure.
      script.remove();
      fail('network');
    }));
    if (!existing) {
      script.id = 'leaflet-js';
      script.src = `${MAP_LIB_BASE}/leaflet.js`;
      document.head.appendChild(script);
    }
  });
  return pending;
}

/** The browser's own position. Rejects with Error('denied' | 'unavailable' | 'timeout' | 'unsupported'). */
export function currentPosition({ timeout = 15_000 } = {}) {
  return new Promise((resolve, reject) => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      reject(new Error('unsupported'));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      (err) => reject(new Error({ 1: 'denied', 2: 'unavailable', 3: 'timeout' }[err?.code] || 'unavailable')),
      { enableHighAccuracy: true, timeout, maximumAge: 60_000 },
    );
  });
}
